import type {
  ClientOrganizationCreateRequest,
  ClientOrganizationCreateRequestTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ModalProps, NativeSyntheticEvent } from 'react-native';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { EdgeInsets } from 'react-native-safe-area-context';

import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useCreateOrganization } from '@/features/api/hooks/grapevine-organization/useCreateOrganization';
import { useGetCreatableOrganizationTypes } from '@/features/api/hooks/grapevine-organization/useGetCreatableOrganizationTypes';
import { resolveOrganizationApiErrorKey } from '@/features/api/utils/organizationApiError';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { AvatarWithMenu } from '@/features/media/components/menu/AvatarWithMenu';
import type { AvatarPickerResult } from '@/features/media/types/picker';
import { EMPTY_AVATAR_PICKER_RESULT } from '@/features/media/types/picker';
import { usePopup } from '@/features/popup/provider/usePopup';
import {
  DEFAULT_FORM_VALUES,
  useCreateOrganizationWizard
} from '@/features/profile-setting/components/organization-creation/hooks/useCreateOrganizationWizard';
import { useEmailExistCheck } from '@/features/profile-setting/components/organization-creation/hooks/useEmailExistCheck';
import { useMapOrganizationTagToOptions } from '@/features/profile-setting/components/organization-creation/hooks/useMapOrganizationTagToOptions';
import { OrganizationAdminStep } from '@/features/profile-setting/components/organization-creation/OrganizationAdminStep';
import { OrganizationCreateFooter } from '@/features/profile-setting/components/organization-creation/OrganizationCreateFooter';
import { OrganizationCreateHeader } from '@/features/profile-setting/components/organization-creation/OrganizationCreateHeader';
import { OrganizationDataStep } from '@/features/profile-setting/components/organization-creation/OrganizationDataStep';
import { OrganizationDataExtensionDebugFrame } from '@/features/profile-setting/components/organization-extensions/OrganizationDataExtensionDebugFrame';
import {
  composeCreateDataPayload,
  getActiveExtensions,
  mergeDataOnTagChange
} from '@/features/profile-setting/components/organization-extensions/registry';
import type { CreateOrganizationForm } from '@/features/profile-setting/schemas/createOrganization';
import {
  AdminType,
  createOrganizationSchema
} from '@/features/profile-setting/schemas/createOrganization';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedModal } from '@/shared/components/ExtendedModal';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    outerWrapper: {
      flex: 1,
      paddingTop: insets.top,
      backgroundColor: isWeb ? 'transparent' : theme.colors.surfaceContainerLow,
      ...(isWeb && {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        elevation: 8
      })
    },
    innerWrapper: {
      flex: 1,
      paddingHorizontal: 16,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    header: {
      paddingTop: 16
    },
    scroll: {
      flex: 1
    },
    scrollContent: {
      flexGrow: 1,
      gap: 16
    },
    image: {
      paddingHorizontal: 48,
      paddingBottom: 8,
      justifyContent: 'center',
      alignItems: 'center'
    },
    form: { gap: 16 }
  });

export const CreateOrganizationModal: FC<
  ModalProps & { organizationId: string }
> = ({ visible, onRequestClose, onDismiss, organizationId }) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-create'
  });
  // Outer popup: the success toast fires just before we close the modal, so it
  // must live on the app-root provider to remain visible after the modal (and
  // its own PopupProvider from ExtendedModal) unmounts.
  const { showToast: showAppToast } = usePopup();

  const closeModal = useCallback(() => {
    onRequestClose?.({} as NativeSyntheticEvent<never>);
  }, [onRequestClose]);

  const handleSuccess = useCallback(
    (name: string) => {
      showAppToast({ message: t('toasts.created', { name }) });
      closeModal();
    },
    [closeModal, showAppToast, t]
  );

  return (
    <ExtendedModal
      visible={visible}
      onDismiss={onDismiss}
      onRequestClose={onRequestClose}
      statusBarTranslucent
      animationType="fade"
    >
      <CreateOrganizationModalContent
        organizationId={organizationId}
        onSuccess={handleSuccess}
        onClose={closeModal}
      />
    </ExtendedModal>
  );
};

type ContentProps = {
  organizationId: string;
  onSuccess: (name: string) => void;
  onClose: () => void;
};

const CreateOrganizationModalContent: FC<ContentProps> = ({
  organizationId,
  onSuccess,
  onClose
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-create'
  });
  const { t: tApi } = useTranslation(LocalizationNameSpace.API);
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  // Inner popup: error toasts must render *inside* the modal, so this must be
  // the ExtendedModal-scoped PopupProvider, not the app-root one.
  const { showToast } = usePopup();
  const { mutate: createOrganization, isPending } = useCreateOrganization();
  const { data: creatableTypes, isLoading: isLoadingCreatableTypes } =
    useGetCreatableOrganizationTypes(organizationId);
  const { userEmail } = useAppState();

  const tagOptions = useMapOrganizationTagToOptions(creatableTypes);

  const schema = useMemo(() => createOrganizationSchema({ t }), [t]);
  const methods = useForm<CreateOrganizationForm>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_FORM_VALUES
  });
  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setError,
    clearErrors,
    setValue,
    getValues,
    reset
  } = methods;

  const adminType = watch('adminType');
  const adminEmail = watch('adminEmail');
  const selectedTags = watch('tags');

  const handleTagsChange = useCallback(
    (tags: ClientOrganizationCreateRequestTagsEnum[]) => {
      setValue(
        'data',
        mergeDataOnTagChange(getValues('data'), getActiveExtensions(tags))
      );
    },
    [setValue, getValues]
  );

  const { step, isStepValid, goToNextStep, goToPreviousStep, resetSteps } =
    useCreateOrganizationWizard({ schema, trigger, watch, adminType });

  const { emailStatus, resetEmailStatus } = useEmailExistCheck({
    adminType,
    adminEmail,
    setError,
    clearErrors,
    t
  });

  const [avatarResult, setAvatarResult] = useState<AvatarPickerResult>(
    EMPTY_AVATAR_PICKER_RESULT
  );

  const resetForm = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
    resetSteps();
    resetEmailStatus();
    setAvatarResult(EMPTY_AVATAR_PICKER_RESULT);
  }, [reset, resetSteps, resetEmailStatus]);

  const onSubmit = useCallback(
    (data: CreateOrganizationForm) => {
      const resolvedAdminEmail =
        data.adminType === AdminType.SELF ? userEmail : (data.adminEmail ?? '');
      const name = data.name.trim();
      const extensionData = composeCreateDataPayload(
        data.data,
        getActiveExtensions(data.tags)
      );
      const clientOrganizationCreateRequest: ClientOrganizationCreateRequest = {
        name,
        adminEmail: resolvedAdminEmail.trim(),
        tags: new Set(data.tags),
        geoAreaIds: new Set(data.geoAreaIds),
        logoMediaItemId: avatarResult.temporaryMediaItemId,
        creatorOrganizationId: organizationId
      };
      if (extensionData) {
        clientOrganizationCreateRequest.data = extensionData;
      }
      createOrganization(clientOrganizationCreateRequest, {
        onSuccess: () => {
          resetForm();
          onSuccess(name);
        },
        onError: (error) => {
          const type = getClientErrorType(error);
          // Cast to string: the new type isn't in the installed generated
          // API package yet (see organizationApiError.ts for the same trick).
          if (
            (type as string | undefined) ===
            'ORGANIZATION_NAME_ALREADY_EXISTS_IN_GEO_AREA'
          ) {
            // Name field lives on step 0; jump back so the field-level
            // error the user needs to act on is actually on screen.
            setError('name', {
              type: 'server',
              message: tApi('organization.errorNameAlreadyExistsInGeoArea')
            });
            goToPreviousStep();
            return;
          }
          const specificKey = resolveOrganizationApiErrorKey(type);
          if (specificKey) {
            showToast({ message: tApi(specificKey) });
          }
        }
      });
    },
    [
      avatarResult.temporaryMediaItemId,
      clearErrors,
      createOrganization,
      goToPreviousStep,
      onSuccess,
      organizationId,
      resetForm,
      setError,
      showToast,
      tApi,
      userEmail
    ]
  );

  const handleSavePress = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit]
  );

  const emailGateOk = adminType !== AdminType.OTHER || emailStatus === 'exists';

  return (
    <FormProvider {...methods}>
      <KeyboardAvoidingView
        style={styles.outerWrapper}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.innerWrapper}>
          <View style={styles.header}>
            <OrganizationCreateHeader
              step={step}
              onBackPress={goToPreviousStep}
              onClosePress={onClose}
            />
          </View>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardDismissMode="none"
            keyboardShouldPersistTaps="always"
          >
            {step === 0 && (
              <OrganizationDataExtensionDebugFrame
                label="Organization (media)"
                color="media"
              >
                <View style={styles.image}>
                  <AvatarWithMenu onChange={setAvatarResult} />
                </View>
              </OrganizationDataExtensionDebugFrame>
            )}
            {step === 0 ? (
              <OrganizationDataStep
                control={control}
                tagOptions={tagOptions}
                isLoadingCreatableTypes={isLoadingCreatableTypes}
                organizationId={organizationId}
                selectedTags={selectedTags}
                onTagsChange={handleTagsChange}
              />
            ) : (
              <OrganizationAdminStep
                control={control}
                adminType={adminType}
                emailStatus={emailStatus}
              />
            )}
            <OrganizationCreateFooter
              step={step}
              isStepValid={isStepValid}
              onNextPress={goToNextStep}
              onSavePress={handleSavePress}
              isSubmitting={isPending}
              emailGateOk={emailGateOk}
            />
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </FormProvider>
  );
};
